import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import bannerImage from "../../../images/Gaudium washcare banner.png";
import { deleteCartItem, loadingCartChange, updateProductSize } from "../../../actions/product";
import { useDispatch } from "react-redux";
import { initCartItemProducts, updateItemSize } from "../../../utils/cartItemProducts";
import {
  calculateCartWeight,
  calculateItemAdditionWeight,
  getMaxConfiguredWeight,
} from "../../../utils/weightHelper";
import ConfirmModal from "../../layout/ConfirmModal";

const QuickViewModal = ({ bundle, onClose, onAddToCart, showAction, user, onSizeUpdate, cartData }) => {
  const dispatch = useDispatch();
  const [quantity] = useState(1);
  const [productSizes, setProductSizes] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [pendingSizes, setPendingSizes] = useState({});
  const [quantities, setQuantities] = useState({});
  // const [selectedSizes, setSelectedSizes] = useState({});
  const [removeItemId, setRemoveItemId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [cartItemProducts, setCartItemProducts] = useState(() =>
    initCartItemProducts(bundle)
  );

  const [ bundleTotalPrice, setBundleTotalPrice ] = useState(bundle.bundle_total)

  useEffect(() => {
    document.body.classList.add("modal-open");
    const initialSizes = {};
    const initialQuantities = {};
    const products = [];
    var bundlePrice = 0;

    const cartItem = cartData?.find(
      item => item.bundleId === bundle.bundle_id
    );

    bundle?.products.forEach((product) => {
      // Size logic
      if (product?.availableSizes?.length > 0) {
        initialSizes[product.product_id] = product.size || "";
      } else {
        product.availableSizes = ["Free-NA"];
        initialSizes[product.product_id] = "Free-NA";
      }

      // Quantity and Price logic
      let currentQty = 1;
      let unitPrice = bundle.isIndividualProduct ? product.unit_price : product.bundle_price;

      if (cartItem?.products?.length > 0) {
        const cartProduct = cartItem.products.find(p => p.productId === product.product_id);
        if (cartProduct) {
          currentQty = cartProduct.totalQuantity;
          unitPrice = bundle.isIndividualProduct ? (cartProduct.unitPrice || product.unit_price) : (cartProduct.bundle_price || product.bundle_price);
        } else {
          currentQty = product.quantity ?? 1;
        }
      } else {
        currentQty = cartItem?.quantity ?? product?.quantity ?? 1;
      }

      initialQuantities[product.product_id] = currentQty;
      bundlePrice += currentQty * unitPrice;

      // Initialize cartItemProducts structure
      products.push({
        cart_item_id: cartItem?.id,
        bundle_id: bundle.bundle_id,
        product_id: product.product_id,
        size: initialSizes[product.product_id],
        base_quantity: product.quantity ?? 0,
        addon_quantity: Math.max(0, currentQty - (product.quantity ?? 0)),
        total_quantity: currentQty,
        quantity: currentQty,
        unit_price: product.unit_price,
        bundle_price: product.bundle_price,
        is_individual_product: bundle.isIndividualProduct,
      });
    });

    if(bundlePrice){
      setBundleTotalPrice(bundlePrice);
    }
    setQuantities(initialQuantities);
    setCartItemProducts(products);

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [bundle, cartData]);

  if (!bundle) return null;

  const handleAddToCart = (bundle) => {
    // Validate that all products with quantity > 0 have a size
    const productsMissingSize = cartItemProducts.filter(p => {
      const quantity = Number(p.total_quantity) || 0;

      if (quantity === 0) return false;

      const currentSize = pendingSizes[p.product_id] || p.size;

      return !currentSize;
    });

    if (productsMissingSize.length > 0) {
      toast.error("Please select sizes for all products", {
        position: "top-right"
      });
      return;
    }

    const maxWeight = getMaxConfiguredWeight(user?.settings);
    if (maxWeight > 0) {
      const itemsList = Array.isArray(cartData) ? cartData : (cartData?.items || []);
      const existingItems = itemsList.filter(
        (it) =>
          !(
            Number(it.bundleId) === Number(bundle?.bundle_id) &&
            Number(it.studentId || it.student?.id) === Number(bundle?.student?.id)
          )
      );
      const currentCartWeight = calculateCartWeight(existingItems).totalKg;
      const itemWeight = calculateItemAdditionWeight(
        bundle,
        cartItemProducts,
        quantity
      ).kg;
      const projectedWeight = Number((currentCartWeight + itemWeight).toFixed(2));
      if (projectedWeight > maxWeight) {
        toast.error(
          `Cannot add to cart: Total cart weight (${projectedWeight} KG) will exceed the maximum allowed limit of ${maxWeight} KG.`,
          { position: "top-right", autoClose: 5000 }
        );
        return;
      }
    }

    setShowConfirmModal(true);
  };

  const confirmAddToCart = () => {
    setShowConfirmModal(false);
    onAddToCart(bundle.bundle_id, quantity, bundle.student.id, pendingSizes, cartItemProducts);
  };

  const handleDownloadImage = () => {
    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = bannerImage;
    link.download = 'Thathva-washcare-instructions.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewSizeChart = (sizeChart) => {
    const img = new Image();
    img.onload = () => {
      window.open(sizeChart, '_blank');
    };
    img.onerror = () => {
      // If JPG fails, try PNG
      const pngImg = new Image();
      pngImg.onload = () => {
        window.open(sizeChart, '_blank');
      };
      pngImg.onerror = () => {
        toast.error("Size chart not found", { position: "top-right" });
      };
      pngImg.src = sizeChart;
    };
    img.src = sizeChart;
  };

  const handleSizeSelect = (productId, size) => {
    setPendingSizes(prev => ({ ...prev, [productId]: size }));
  };

  const handleSaveSizeClick = async (productId) => {
    const size = pendingSizes[productId];
    if (!size) return;

    // Set loading state for this product
    setLoadingStates(prev => ({ ...prev, [productId]: true }));

    try {
      // Update local state
      setProductSizes(prev => ({ ...prev, [productId]: size }));

      const sizeData = {
        size,
        studentId: bundle.student.id,
        productId
      };

      await updateProductSize(sizeData);

      // Update the bundle object with the new size
      if (onSizeUpdate) {
        const updatedBundle = {
          ...bundle,
          products: bundle?.products?.map(product => {
            if (product.product_id === productId) {
              return { ...product, size };
            }
            return product;
          })
        };
        onSizeUpdate(updatedBundle);
      }
      setCartItemProducts(prev =>
        updateItemSize(prev, productId, size)
      );

      // Clear pending size after successful save
      setPendingSizes(prev => {
        const newState = { ...prev };
        delete newState[productId];
        return newState;
      });
    } catch (error) {
      setProductSizes(prev => ({ ...prev, [productId]: '' }));
      toast.error("Failed to update size", { position: "top-right" });
    } finally {
      setLoadingStates(prev => ({ ...prev, [productId]: false }));
    }
  };


  const groupedProducts = bundle.products
    // .filter(item => !item.optional)
    .reduce((acc, item) => {
      const category = item.product_category || "Others";

      if (!acc[category]) {
        acc[category] = [];
      }

      acc[category].push(item);
      return acc;
    }, {});

  const categoryOrder = Object.keys(groupedProducts).sort();

  const handleAddonChange = (item, value) => {
    const numericValue = Number(value) || 0;

    const minAllowed = item.selection_group ? 0 : item.quantity;

    const finalValue = Math.min(
      item.max_quantity,
      Math.max(minAllowed, numericValue)
    );

    const groupId = item.selection_group;

    let updatedQuantities = {
      ...quantities,
      [item.product_id]: finalValue,
    };

    // 🔥 GROUP LOGIC
    if (groupId) {
      const groupItems = bundle.products.filter(
        (p) => p.selection_group === true
      );

      const groupTotal = groupItems.reduce((sum, p) => {
        const qty =
          p.product_id === item.product_id
            ? finalValue
            : updatedQuantities[p.product_id] ?? p.quantity;

        return sum + qty;
      }, 0);

      // 🚨 If all become zero → auto assign another item
      if (groupTotal < 1) {
        const fallbackItem = groupItems.find(
          (p) => p.product_id !== item.product_id
        );

        if (fallbackItem) {
          updatedQuantities[fallbackItem.product_id] =
            fallbackItem.quantity; // base minimum
        }
        toast.error("Please select at least one product from this group.", {
          position: "top-right",
        });
      }
    }

    // 🔥 Update quantity state
    setQuantities(updatedQuantities);

    // 🔥 Recalculate cart
    setCartItemProducts((prev) => {
      const updated = prev.map((p) => {
        const qty = updatedQuantities[p.product_id] ?? p.total_quantity;

        return {
          ...p,
          total_quantity: qty,
          addon_quantity: Math.max(0, qty - p.base_quantity),
        };
      });

      // 🔥 Recalculate total price properly
      let totalPrice = 0;

      updated.forEach((p) => {
        var price = 0;
        if(p.is_individual_product){
          price = p.unit_price
        }else{
          price = p.bundle_price
        }
          // Normal product → only addon affects price
          totalPrice += p.total_quantity * price;
      });

      setBundleTotalPrice(totalPrice);

      return updated;
    });
  };

  // const handleSizeChange = (bundleProduct, size) => {
  //   setSelectedSizes((prev) => ({
  //     ...prev,
  //     [bundleProduct.bundle_id]: size,
  //   }));

  //   const sizeData = {
  //     size: size,
  //     studentId: bundle.student.id,
  //     productId: bundleProduct?.products[0]?.product_id
  //   };
  //   updateProductSize(sizeData, false);
  // };


  const cartBundleIds = new Set(cartData?.map(item => item.bundleId));

  const isProductInCart = (productId) => cartBundleIds.has(productId);

  const handleRemove = (bundleId) => {
    dispatch(loadingCartChange(true));

    dispatch(deleteCartItem({ parentId: user.id, bundleId: bundleId }))
      .then(() => {
        toast.info("Item removed from cart!", { position: "top-right" });
      })
      .catch(() => {
        toast.error("Failed to remove item from cart!", {
          position: "top-right",
        });
      });
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      role="dialog"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg w-m-50"
        role="document"
      >
        <div className="modal-content">
          <div className="modal-header">
            {user ? (
              <h5 className="modal-title">{bundle.bundle_name ?? bundle.display_name}</h5>
            ) : (
              <h5 className="modal-title">{bundle.bundle_name}</h5>
            )}
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
            ></button>
          </div>
          <div
            className="modal-body"
            style={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            <img
              src={bundle.image}
              className="img-fluid mb-3"
              alt={bundle.bundle_name}
              style={{ maxHeight: "400px", width: "100%" }}
            />
            <div className="row g-3 mb-4 mt-1">
              <div className="col-md-7">
                <div className="card h-100 border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-person-badge text-primary me-2"></i>
                      <h6 className="card-subtitle text-muted fw-bold text-uppercase mb-0" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Student Information</h6>
                    </div>

                    <div className="row g-2">
                      {showAction && (
                        <div className="col-12 border-bottom pb-2 mb-2">
                          <small className="text-secondary d-block" style={{ fontSize: '0.75rem' }}>Student Name</small>
                          <span className="fw-bold text-dark">{bundle.student?.studentName}</span>
                        </div>
                      )}

                      <div className="col-6">
                        <small className="text-secondary d-block" style={{ fontSize: '0.75rem' }}>Gender</small>
                        <span className="fw-bold text-dark">{bundle.gender}</span>
                      </div>

                      {showAction && (
                        <div className="col-6">
                          <small className="text-secondary d-block" style={{ fontSize: '0.75rem' }}>Class</small>
                          <span className="fw-bold text-dark">{bundle.class_name}</span>
                        </div>
                      )}

                      {showAction && (
                        <div className="col-6">
                          <small className="text-secondary d-block" style={{ fontSize: '0.75rem' }}>House</small>
                          <span className="fw-bold text-dark">{bundle.student?.house || 'N/A'}</span>
                        </div>
                      )}
{/* 
                      <div className="col-6">
                        <small className="text-secondary d-block" style={{ fontSize: '0.75rem' }}>Class Compatibility</small>
                        <span className="fw-bold text-dark">{bundle.applicable_classes}</span>
                      </div> */}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-5">
                <div className="card h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)', borderRadius: '12px', borderLeft: '4px solid #0d6efd !important' }}>
                  <div className="card-body p-3 d-flex flex-column justify-content-center">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-cart-check text-primary me-2"></i>
                      <h6 className="card-subtitle text-muted fw-bold text-uppercase mb-0" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Bundle Total</h6>
                    </div>
                    <div className="mt-2">
                      <span className="text-primary fw-bold display-6 d-block" style={{ fontSize: '1.8rem' }}>
                        ₹{bundleTotalPrice.toLocaleString('en-IN')}
                      </span>
                      <small className="text-muted italic" style={{ fontSize: '0.7rem' }}>Inclusive of all taxes</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <h6 className="mt-4">Bundle Contents</h6>
            {bundle.products?.some(p => p.selection_group) && (
              <div className="alert alert-warning py-2 px-3 mb-3 border-0 shadow-sm d-flex align-items-center" style={{ fontSize: '0.85rem', borderRadius: '8px' }}>
                <i className="bi bi-info-circle-fill me-2 fs-5 text-warning"></i>
                <div>
                  <strong>Note:</strong> This bundle contains grouped items. You must select <strong>at least one product</strong> from the group marked below.
                </div>
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-striped">
                <thead className="table-light">
                  <tr className="text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    {user ? (
                      <th className="py-3 px-3"><i className="bi bi-box-seam me-2"></i>Product</th>
                    ) : (
                      <>
                        <th className="py-3 px-3"><i className="bi bi-box-seam me-2"></i>Product</th>
                        <th className="py-3"><i className="bi bi-tag me-2"></i>Display Name</th>
                      </>
                    )}

                    <th className="py-3">Size Guide</th>
                    {user ? (
                        <th className="py-3 text-center"><i className="bi bi-hash me-1"></i>Qty</th>
                      ) : (
                        <>
                          <th className="py-3">Qty</th>
                          <th className="py-3">Max Qty</th>
                        </>
                      )}
                    {user && (
                      <>
                        <th className="py-3"><i className="bi bi-currency-rupee me-1"></i>Amount</th>
                        <th className="py-3"><i className="bi bi-check-circle me-2"></i>Available Size</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {categoryOrder?.map(category =>
                    groupedProducts[category]?.length ? (
                      <React.Fragment key={category}>
                        {groupedProducts[category]?.map((item, index) => (
                          <tr key={index}>
                            {user ? (
                              <td>{item.product_display_name ?? item.product_name}</td>
                            ) : (
                              <>
                                <td>{item.product_name}</td>
                                <td>{item.product_display_name}
                                  {item.optional && (
                                    <div style={{ fontSize: '12px', color: 'gray' }}>
                                      Optional
                                    </div>
                                  )}
                                </td>
                              </>
                            )}

                            <td>
                              {item.size_chart && (
                                <button
                                  className="text-primary"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleViewSizeChart(item.size_chart);
                                  }}
                                >
                                  Size Chart
                                </button>
                              )}
                            </td>

                            {user ? (
                              <td className="align-middle" style={{ minWidth: '150px' }}>
                                {item.selection_group && (
                                  <div className="mb-2">
                                    <span className="badge rounded-pill bg-danger-subtle text-danger border border-danger-subtle d-inline-flex align-items-center py-1 px-2" style={{ fontSize: '0.65rem', fontWeight: '600', letterSpacing: '0.3px' }}>
                                      <i className="bi bi-exclamation-circle-fill me-1"></i>
                                      Select at least one from this group
                                    </span>
                                  </div>
                                )}
                                <div className="d-flex align-items-center">
                                  <div className="input-group input-group-sm shadow-sm" style={{ width: "110px", borderRadius: '8px', overflow: 'hidden', border: '1px solid #dee2e6' }}>
                                    {/* MINUS */}
                                    <button
                                      type="button"
                                      className="btn btn-light border-0 d-flex align-items-center justify-content-center px-2"
                                      style={{ backgroundColor: '#f8f9fa' }}
                                      onClick={() => {
                                        const currentQty = quantities[item.product_id] ?? item.quantity;
                                        handleAddonChange(
                                          item,
                                          Math.max(
                                            item.selection_group ? 0 : item.quantity,
                                            currentQty - 1
                                          )
                                        );
                                      }}
                                      disabled={
                                        (quantities[item.product_id] ?? item.quantity) <=
                                        (item.selection_group ? 0 : item.quantity)
                                      }
                                    >
                                      <i className="bi bi-dash-lg" style={{ fontSize: '0.8rem' }}></i>
                                    </button>

                                    {/* INPUT */}
                                    <input
                                      type="number"
                                      className="form-control text-center border-0 fw-bold bg-white"
                                      style={{ fontSize: '0.9rem', padding: '0.25rem 0' }}
                                      value={quantities[item.product_id] ?? item.quantity}
                                      min={item.selection_group ? 0 : item.quantity}
                                      max={item.max_quantity}
                                      readOnly
                                    />

                                    {/* PLUS */}
                                    <button
                                      type="button"
                                      className="btn btn-light border-0 d-flex align-items-center justify-content-center px-2"
                                      style={{ backgroundColor: '#f8f9fa' }}
                                      onClick={() =>
                                        handleAddonChange(
                                          item,
                                          Math.min(
                                            item.max_quantity,
                                            (quantities[item.product_id] || item.quantity) + 1
                                          )
                                        )
                                      }
                                      disabled={
                                        (quantities[item.product_id] ?? item.quantity) >= item.max_quantity
                                      }
                                    >
                                      <i className="bi bi-plus-lg" style={{ fontSize: '0.8rem' }}></i>
                                    </button>
                                  </div>
                                  <div className="ms-2">
                                     <small className="text-muted" style={{ fontSize: '0.7rem', display: 'block' }}>
                                       Max: {item.max_quantity}
                                     </small>
                                  </div>
                                </div>
                                </td>
                            ) : (
                              <>
                                <td>{item.quantity}</td>
                                <td>{item.max_quantity}</td>
                              </>
                            )}

                            {user && (
                              <>
                              <td className="align-middle fw-bold">
                                ₹{((quantities[item.product_id] ?? item.quantity) * (bundle.isIndividualProduct ? item.unit_price : item.bundle_price)).toLocaleString('en-IN')}
                              </td>
                              <td className="align-middle">
                                <div className="d-flex flex-column" style={{ minWidth: '160px' }}>
                                  <div className="d-flex align-items-center">
                                    <div className="flex-grow-1 position-relative">
                                      {item.availableSizes?.length ? (
                                        <select
                                          className="form-select form-select-sm border-0 shadow-sm bg-light fw-medium"
                                          style={{ 
                                            borderRadius: '8px', 
                                            paddingRight: '30px',
                                            height: '36px'
                                          }}
                                          value={
                                            pendingSizes[item.product_id] ||
                                            productSizes[item.product_id] ||
                                            item.size ||
                                            ""
                                          }
                                          onChange={(e) =>
                                            handleSizeSelect(item.product_id, e.target.value)
                                          }
                                        >
                                          <option value="">Select size</option>
                                         {item?.availableSizes
                                            ?.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                                            .map((size) => (
                                              <option key={size} value={size}>
                                                {size}
                                              </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          className="form-control form-control-sm border-0 shadow-sm bg-light fw-medium"
                                          style={{ 
                                            borderRadius: '8px',
                                            height: '36px'
                                          }}
                                          placeholder="Size..."
                                          value={
                                            pendingSizes[item.product_id] ||
                                            productSizes[item.product_id] ||
                                            item.size ||
                                            ""
                                          }
                                          onChange={(e) =>
                                            handleSizeSelect(item.product_id, e.target.value)
                                          }
                                        />
                                      )}
                                    </div>
                                    
                                    {(pendingSizes[item.product_id]) && (
                                      <button
                                        className="btn btn-sm btn-success ms-2 shadow-sm d-flex align-items-center justify-content-center"
                                        style={{ 
                                          borderRadius: '8px', 
                                          width: '36px', 
                                          height: '36px',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onClick={() => handleSaveSizeClick(item.product_id)}
                                        disabled={loadingStates[item.product_id]}
                                        title="Save Size"
                                      >
                                        {loadingStates[item.product_id] ? (
                                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        ) : (
                                          <i className="bi bi-check2" style={{ fontSize: '1.2rem' }}></i>
                                        )}
                                      </button>
                                    )}
                                  </div>
                                  
                                  {pendingSizes[item.product_id] && (
                                    <div className="mt-1">
                                      <span className="text-warning" style={{ fontSize: '0.65rem', fontWeight: '600' }}>
                                        <i className="bi bi-arrow-right-circle-fill me-1"></i>
                                        Pending changes...
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </React.Fragment>
                    ) : null
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3 text-center">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={handleDownloadImage}
              >
                <i className="bi bi-download me-1"></i>
                Download Washcare Instructions
              </button>
            </div>
          </div>

          <div className="modal-footer">
            {showAction && (
              <>
                {/* Quantity Selection Dropdown */}
                {/* <div className="d-flex align-items-center">
                  <label className="me-2" htmlFor="quantitySelect">
                    Quantity:
                  </label>
                  <select
                    id="quantitySelect"
                    className="form-select me-3"
                    style={{ width: "80px" }}
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                  >
                    {[...Array(10)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                </div> */}
              </>
            )}

            <button type="button" className="btn btn-danger" onClick={onClose}>
              Close
            </button>
            {showAction && (
              !bundle.isAlreadyPurchased ? (
                <>
                  <button
                    type="button"
                    className={`btn ${isProductInCart(bundle.bundle_id)
                      ? "btn-primary"
                      : "btn-primary"
                      }`}
                    onClick={() => handleAddToCart(bundle)}
                  >
                    {isProductInCart(bundle.bundle_id)
                      ? "Update Bundle"
                      : "Add Bundle"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={bundle.isAlreadyPurchased}
                >
                  Already Purchased
                </button>
              )
            )}
          </div>

          {removeItemId && (
            <div className="modal fade show d-block" tabIndex="-1">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content rounded-4">
                  <div className="modal-header">
                    <h5 className="modal-title">Remove Item</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setRemoveItemId(null)}
                    />
                  </div>
                  <div className="modal-body">
                    Are you sure you want to remove this item from your cart?
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setRemoveItemId(null)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => {
                        handleRemove(removeItemId);
                        setRemoveItemId(null);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <ConfirmModal
            show={showConfirmModal}
            onClose={() => setShowConfirmModal(false)}
            onConfirm={confirmAddToCart}
            title="Verify Sizes"
            message="Please check sizes carefully before placing your order. There will be no exchanges or returns once the order has been submitted."
            confirmButtonText="Confirm & Add to Cart"
            cancelButtonText="Review Sizes"
          />
        </div>
      </div>
    </div>
  );
};

export default QuickViewModal;
